import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { format } from 'date-fns';
import { takeUntil } from 'rxjs';
import { BaseComponent, FORMAT_DATE } from '../../common';

@Component({
  selector: 'hdis-multi-date-field',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatChipsModule, MatIconModule, MatDatepickerModule],
  templateUrl: './multi-date-field.component.html',
  styleUrls: ['./multi-date-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => MultiDateFieldComponent), multi: true }],
})
export class MultiDateFieldComponent extends BaseComponent implements ControlValueAccessor {
  @Input() label = 'Dates';

  @Input() placeholder = 'New date...';

  @Input()
  get inputVisible(): boolean {
    return this._inputVisible;
  }

  set inputVisible(value: boolean) {
    this._inputVisible = coerceBooleanProperty(value);
  }

  private _inputVisible = false;

  control = new FormControl(['']);

  constructor() {
    super();
    this.control.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((dates) => this.propagateFn?.(dates));
  }

  propagateFn: (dates: string[]) => void;

  writeValue(value: string[]): void {
    this.control.setValue(value);
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.propagateFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    // throw new Error('Method not implemented.');
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.control.disable() : this.control.enable();
  }

  removeDate(date: string) {
    this.control.patchValue(this.control.value.filter((d: string) => d !== date));
  }

  onChipChange(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value && !this.control.value.includes(value)) this.control.patchValue([...this.control.value, value].sort((a, b) => a.localeCompare(b)));
    // Clear the input value
    event.chipInput!.clear();
  }

  onDateChange(event: MatDatepickerInputEvent<Date>) {
    if (event.value) {
      const formattedDate = format(event.value, FORMAT_DATE);
      if (!this.control.value.includes(formattedDate)) {
        this.control.patchValue([...this.control.value, formattedDate].sort((a, b) => a.localeCompare(b)));
      }
    }
    // Clear the input value

    event.target.value = '';
  }
}
